import React from 'react';

import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import CustomModal from 'components/CustomModal';
import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import EntityDropdownField from 'components/SharedFormComponents/EntityDropdownField';

import { EntityModules, ProtectorType } from 'core/constants';
import routes from 'core/routes';

import { EntityEnumsOptions } from 'utils/api/modules/entities/entities.types';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { useStore } from 'utils/hooks/store';
import { ChoiceValue } from 'utils/library';

import { HAZARD_FIELD } from '../../constants';
import { LoadError } from '../../styles';

import FormRow from './FormRow';
import { CreateHazardValues, createHazardSchema, toFieldValues } from './schema';

type CreateHazardModalProps = {
  onClose: () => void;
};

const initialValues: CreateHazardValues = {
  name: '',
  category: null,
  whoMightBeHarmed: '',
  howMightBeHarmed: '',
};

const CreateHazardModal: React.FunctionComponent<CreateHazardModalProps> = ({ onClose }): React.ReactElement => {
  const { hazardDetails, entityFields, routing } = useStore();

  const [fieldsRequested, setFieldsRequested] = React.useState(false);

  const loadFields = React.useCallback(async (): Promise<void> => {
    setFieldsRequested(false);
    await entityFields.loadFields(EntityModules.Hazards);
    setFieldsRequested(true);
  }, [entityFields]);

  React.useEffect(() => {
    loadFields();
  }, [loadFields]);

  const categoryField = entityFields.data.find((field) => field.id === HAZARD_FIELD.category);
  const categories = ((categoryField?.enum as EntityEnumsOptions[] | undefined) ?? []).filter(
    (option) => option.active
  );

  const onSubmit = async (
    values: CreateHazardValues,
    { setErrors, setSubmitting }: FormikHelpers<CreateHazardValues>
  ): Promise<void> => {
    const created = await hazardDetails.createHazard(values.name.trim(), toFieldValues(values));

    if (typeof created === 'string') {
      onClose();
      routing.push(reverse(routes.dashboard.risks.hazardDetails, { entityId: created }));
      return;
    }

    const serverErrors: FormikErrors<CreateHazardValues> = {
      name: hazardDetails.fieldErrors[HAZARD_FIELD.name],
      category: hazardDetails.fieldErrors[HAZARD_FIELD.category],
      whoMightBeHarmed: hazardDetails.fieldErrors[HAZARD_FIELD.whoMightBeHarmed],
      howMightBeHarmed: hazardDetails.fieldErrors[HAZARD_FIELD.howMightBeHarmed],
    };
    setErrors(serverErrors);
    setSubmitting(false);
  };

  if (!categoryField) {
    return (
      <CustomModal
        openModal
        onClose={onClose}
        modalTitle="New hazard"
        cancelButton={
          <Button variant="outlined" size="large" onClick={onClose}>
            Cancel
          </Button>
        }
      >
        {fieldsRequested ? (
          <LoadError data-testid="create-hazard-load-error">
            <span>The form could not be loaded.</span>
            <Button variant="contained" onClick={loadFields}>
              Retry
            </Button>
          </LoadError>
        ) : (
          <CenteredCircularProgress />
        )}
      </CustomModal>
    );
  }

  return (
    <Formik<CreateHazardValues>
      initialValues={initialValues}
      validationSchema={createHazardSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting, isValid, handleSubmit }) => {
        const showError = (key: keyof CreateHazardValues): string | undefined =>
          touched[key] ? (errors[key] as string | undefined) : undefined;

        return (
          <CustomModal
            openModal
            onClose={onClose}
            modalTitle="New hazard"
            cancelButton={
              <Button variant="outlined" size="large" onClick={onClose}>
                Cancel
              </Button>
            }
            submitButton={
              <Button
                variant="contained"
                size="large"
                data-testid="create-hazard-submit"
                disabled={isSubmitting || !isValid}
                onClick={() => handleSubmit()}
              >
                Add hazard
              </Button>
            }
          >
            <div data-testid="create-hazard-modal">
              <FormRow testId="create-hazard-name" label="Hazard">
                <CustomTextInputField
                  value={values.name}
                  description="Hazard"
                  onChange={(value: AnswerTypes) => setFieldValue('name', value)}
                  requiredNotFilled={Boolean(showError('name'))}
                  errorMessage={showError('name')}
                  inputProps={{ autoFocus: true }}
                />
              </FormRow>
              <FormRow testId="create-hazard-category" label="Category">
                <EntityDropdownField
                  type={ProtectorType.SingleChoice}
                  value={values.category}
                  items={{ enum: categories }}
                  description="Category"
                  customPlaceholder="Select category"
                  requiredNotFilled={Boolean(showError('category'))}
                  onChange={(value) => {
                    setFieldTouched('category', true, false);
                    setFieldValue('category', value as unknown as ChoiceValue);
                  }}
                />
              </FormRow>
              <FormRow testId="create-hazard-who" label="Who might be harmed">
                <CustomTextInputField
                  value={values.whoMightBeHarmed}
                  description="Who might be harmed"
                  multiline
                  minRows={3}
                  onChange={(value: AnswerTypes) => setFieldValue('whoMightBeHarmed', value)}
                  requiredNotFilled={Boolean(showError('whoMightBeHarmed'))}
                  errorMessage={showError('whoMightBeHarmed')}
                />
              </FormRow>
              <FormRow testId="create-hazard-how" label="How might they be harmed">
                <CustomTextInputField
                  value={values.howMightBeHarmed}
                  description="How might they be harmed"
                  multiline
                  minRows={3}
                  onChange={(value: AnswerTypes) => setFieldValue('howMightBeHarmed', value)}
                  requiredNotFilled={Boolean(showError('howMightBeHarmed'))}
                  errorMessage={showError('howMightBeHarmed')}
                />
              </FormRow>
            </div>
          </CustomModal>
        );
      }}
    </Formik>
  );
};

export default observer(CreateHazardModal);
